import { useState } from 'react'
import { Inbox, ExternalLink, EyeOff, Eye, MoreHorizontal, MessageSquareReply } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { AppPagination } from '@/components/shared/app-pagination'
import { EmptyState } from '@/components/shared/empty-state'
import { ModerationFilters } from './moderation-filters'
import { ReplyDialog } from './reply-dialog'
import { useModerations, useHideComment, useUnhideComment } from '@/hooks/use-social-listening'
import { cn } from '@/lib/utils'
import type {
  ModerationFilters as ModerationFiltersType,
  CommentModeration,
} from '@/lib/api/modules/social-listening'

const INTENT_CONFIG = {
  complaint: { label: 'Reclamação', variant: 'destructive' as const },
  spam: { label: 'Spam', variant: 'secondary' as const },
  question: { label: 'Pergunta', variant: 'outline' as const },
  positive: { label: 'Elogio', variant: 'outline' as const },
  neutral: { label: 'Neutro', variant: 'outline' as const },
} as const

const ACTION_CONFIG = {
  hidden: { label: 'Ocultado', variant: 'default' as const },
  failed_to_hide: { label: 'Falhou', variant: 'destructive' as const },
  none: { label: 'Sem ação', variant: 'secondary' as const },
  unhidden: { label: 'Desocultado', variant: 'outline' as const },
} as const

function formatRelativeDate(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMin = Math.floor(diffMs / 60_000)
  const diffHrs = Math.floor(diffMs / 3_600_000)
  const diffDays = Math.floor(diffMs / 86_400_000)

  if (diffMin < 1) return 'agora'
  if (diffMin < 60) return `${diffMin}min`
  if (diffHrs < 24) return `${diffHrs}h`
  if (diffDays < 30) return `${diffDays}d`
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
}

function scoreColorClass(score: number | null): string {
  if (score === null) return 'bg-muted-foreground/40'
  if (score > 0.8) return 'bg-red-500'
  if (score > 0.5) return 'bg-amber-500'
  return 'bg-emerald-500'
}

function ModerationRow({ item }: { item: CommentModeration }) {
  const hideComment = useHideComment()
  const unhideComment = useUnhideComment()
  const [confirmAction, setConfirmAction] = useState<'hide' | 'unhide' | null>(null)
  const [replyOpen, setReplyOpen] = useState(false)

  const actionCfg = ACTION_CONFIG[item.action]
  const intentCfg = item.intent ? INTENT_CONFIG[item.intent] : null

  const canHide =
    item.action === 'none' || item.action === 'failed_to_hide' || item.action === 'unhidden'
  const canUnhide = item.action === 'hidden'
  const isPending = hideComment.isPending || unhideComment.isPending

  const handleConfirm = () => {
    if (confirmAction === 'hide')
      hideComment.mutate(item.id, { onSettled: () => setConfirmAction(null) })
    if (confirmAction === 'unhide')
      unhideComment.mutate(item.id, { onSettled: () => setConfirmAction(null) })
  }

  return (
    <>
      <TableRow className="transition-colors hover:bg-muted/50">
        {/* Comment text + AI reason */}
        <TableCell className="max-w-sm">
          <div className="space-y-0.5">
            <p className="text-sm whitespace-normal wrap-break-word">{item.commentText}</p>
            {item.sentimentReason && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-1.5">
                    <span
                      className={cn(
                        'h-1.5 w-1.5 rounded-full shrink-0',
                        scoreColorClass(item.sentimentScore)
                      )}
                      aria-hidden="true"
                    />
                    <span className="text-xs text-muted-foreground line-clamp-1">
                      {item.sentimentReason}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm">
                  <p className="text-xs">
                    IA: {item.sentimentReason} —{' '}
                    {item.sentimentScore != null && `${Math.round(item.sentimentScore * 100)}%`}
                  </p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TableCell>

        {/* Author */}
        <TableCell className="text-sm text-muted-foreground">
          {item.authorUsername ? `@${item.authorUsername}` : '—'}
        </TableCell>

        {/* Intent badge */}
        <TableCell>
          {intentCfg ? (
            <Badge variant={intentCfg.variant}>{intentCfg.label}</Badge>
          ) : (
            <span className="text-sm text-muted-foreground">—</span>
          )}
        </TableCell>

        {/* Post link */}
        <TableCell>
          {item.postPermalink ? (
            <a
              href={item.postPermalink}
              target="_blank"
              rel="noopener noreferrer"
              className="text-muted-foreground hover:text-foreground transition-colors"
              aria-label="Abrir post no Instagram"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
            </a>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </TableCell>

        {/* Action status + Respondido badge */}
        <TableCell>
          <div className="flex items-center flex-wrap gap-1">
            <Badge variant={actionCfg.variant}>{actionCfg.label}</Badge>
            {item.repliedAt && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="ml-1 text-xs">
                    Respondido
                  </Badge>
                </TooltipTrigger>
                <TooltipContent className="max-w-sm">
                  <p className="text-sm">{item.replyText}</p>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </TableCell>

        {/* Date */}
        <TableCell className="text-sm text-muted-foreground hidden md:table-cell">
          {formatRelativeDate(item.createdAt)}
        </TableCell>

        {/* Actions dropdown */}
        <TableCell>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                aria-label="Abrir menu de ações"
              >
                <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setReplyOpen(true)}>
                <MessageSquareReply className="mr-2 h-4 w-4" aria-hidden="true" />
                Responder
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {canHide && (
                <DropdownMenuItem onClick={() => setConfirmAction('hide')} disabled={isPending}>
                  <EyeOff className="mr-2 h-4 w-4" aria-hidden="true" />
                  {item.action === 'failed_to_hide' ? 'Tentar novamente' : 'Ocultar'}
                </DropdownMenuItem>
              )}
              {canUnhide && (
                <DropdownMenuItem onClick={() => setConfirmAction('unhide')} disabled={isPending}>
                  <Eye className="mr-2 h-4 w-4" aria-hidden="true" />
                  Desocultar
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <AlertDialog
            open={confirmAction !== null}
            onOpenChange={(open) => !open && setConfirmAction(null)}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {confirmAction === 'unhide' ? 'Desocultar comentário' : 'Ocultar comentário'}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {confirmAction === 'unhide'
                    ? 'O comentário voltará a ser visível no post do Instagram. Tem certeza?'
                    : 'O comentário será ocultado no post do Instagram. Tem certeza?'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  disabled={isPending}
                  onClick={(e) => {
                    e.preventDefault()
                    handleConfirm()
                  }}
                >
                  {isPending ? 'Processando...' : 'Confirmar'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TableCell>
      </TableRow>

      <ReplyDialog item={item} open={replyOpen} onOpenChange={setReplyOpen} />
    </>
  )
}

function LoadingSkeleton() {
  return (
    <>
      {Array.from({ length: 5 }).map((_, i) => (
        <TableRow key={i}>
          <TableCell>
            <div className="space-y-1.5">
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-3 w-36" />
            </div>
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-20" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-20 rounded-sm" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-4 w-4" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-5 w-16 rounded-sm" />
          </TableCell>
          <TableCell className="hidden md:table-cell">
            <Skeleton className="h-4 w-10" />
          </TableCell>
          <TableCell>
            <Skeleton className="h-8 w-8 rounded-md" />
          </TableCell>
        </TableRow>
      ))}
    </>
  )
}

export function ModerationTable() {
  const [filters, setFilters] = useState<ModerationFiltersType>({ page: 1, limit: 20 })
  const { data, isLoading } = useModerations(filters)

  const items = data?.data ?? []
  const meta = data?.meta

  return (
    <div className="space-y-4">
      <ModerationFilters filters={filters} onChange={setFilters} />

      {!isLoading && items.length === 0 ? (
        <EmptyState
          icon={Inbox}
          title="Nenhum comentário processado"
          description="Comentários serão classificados e gerenciados automaticamente quando chegarem via webhook."
        />
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-75">Comentário</TableHead>
              <TableHead>Autor</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead className="w-10">Post</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="hidden md:table-cell">Data</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              items.map((item) => <ModerationRow key={item.id} item={item} />)
            )}
          </TableBody>
        </Table>
      )}

      <AppPagination
        meta={meta}
        onPageChange={(page) => setFilters((f) => ({ ...f, page }))}
        isLoading={isLoading}
      />
    </div>
  )
}
